import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import SeverityBadge from "../components/SeverityBadge";
import { formatIDR, formatPercent, formatDate } from "../lib/format";
import { AlertTriangle, TrendingUp, Wallet, Activity, Users, FileWarning } from "lucide-react";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/summary")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- load once on mount

  if (loading) {
    return <div className="p-8 text-sm text-muted-foreground" data-testid="dashboard-loading">Memuat dashboard…</div>;
  }
  if (!data) return null;

  const utilizationRatio = data.utilization_ratio || 0;
  const ratioColor = utilizationRatio >= 0.9 ? "text-[#D0021B]" : utilizationRatio >= 0.7 ? "text-[#F5A623]" : "text-emerald-700";

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="dashboard-page">
      <PageHeader
        title="Dashboard Risk & Limit"
        subtitle="Ringkasan eksposur, limit, dan early warning per hari ini"
        testId="dashboard-header"
      />

      {/* EWS alerts strip */}
      {data.alert_count > 0 && (
        <div className="mb-6 flex items-stretch gap-3" data-testid="ews-strip">
          <div className="flex-1 bg-card border-l-4 border-[#D0021B] border-y border-r border-border rounded-md p-4 flex items-center gap-4">
            <AlertTriangle className="h-6 w-6 text-[#D0021B]" />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Early Warning System</div>
              <div className="text-base font-semibold mt-0.5">
                <span className="mono">{data.alert_count}</span> alert terbuka
                <span className="ml-3 text-xs text-muted-foreground font-normal">
                  Kritis: <span className="mono">{data.alerts_by_severity?.critical || 0}</span> ·
                  Peringatan: <span className="mono">{data.alerts_by_severity?.warning || 0}</span>
                </span>
              </div>
            </div>
            <button
              className="text-xs font-semibold uppercase tracking-wider border border-foreground px-3 py-1.5 rounded hover:bg-foreground hover:text-background transition-colors"
              onClick={() => navigate("/alerts")}
              data-testid="ews-view-all-button"
            >
              Lihat Semua →
            </button>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="kpi-card" data-testid="kpi-approved-limit">
          <div className="flex items-center justify-between">
            <div className="kpi-label">Total Approved Limit</div>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="kpi-value">{formatIDR(data.total_approved_limit, { compact: true })}</div>
          <div className="kpi-sub">{formatIDR(data.total_approved_limit)}</div>
        </div>
        <div className="kpi-card" data-testid="kpi-utilized-limit">
          <div className="flex items-center justify-between">
            <div className="kpi-label">Total Utilized</div>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="kpi-value">{formatIDR(data.total_utilized_limit, { compact: true })}</div>
          <div className={`kpi-sub mono ${ratioColor}`}>Utilisasi {formatPercent(utilizationRatio)}</div>
        </div>
        <div className="kpi-card" data-testid="kpi-available-limit">
          <div className="flex items-center justify-between">
            <div className="kpi-label">Total Available</div>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="kpi-value">{formatIDR(data.total_available_limit, { compact: true })}</div>
          <div className="kpi-sub">Sisa kapasitas</div>
        </div>
        <div className="kpi-card" data-testid="kpi-exposure">
          <div className="flex items-center justify-between">
            <div className="kpi-label">Total Eksposur</div>
            <FileWarning className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="kpi-value">{formatIDR(data.total_exposure, { compact: true })}</div>
          <div className="kpi-sub">Outstanding amount</div>
        </div>
      </div>

      {/* Sub KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="kpi-card" data-testid="kpi-active-borrowers">
          <div className="kpi-label flex items-center gap-1"><Users className="h-3 w-3" />Borrower Aktif</div>
          <div className="kpi-value text-xl">{data.active_borrowers}</div>
        </div>
        <div className="kpi-card" data-testid="kpi-active-facilities">
          <div className="kpi-label">Fasilitas Aktif</div>
          <div className="kpi-value text-xl">{data.active_facilities}</div>
        </div>
        <div className="kpi-card" data-testid="kpi-limit-breach">
          <div className="kpi-label">Limit Breach</div>
          <div className={`kpi-value text-xl ${data.limit_breach_count > 0 ? "text-[#D0021B]" : ""}`}>
            {data.limit_breach_count}
          </div>
          {data.excess_acknowledged_count > 0 && (
            <div className="kpi-sub text-[#F5A623]">+{data.excess_acknowledged_count} excess (ack)</div>
          )}
        </div>
        <div className="kpi-card" data-testid="kpi-covenant-breach">
          <div className="kpi-label">Covenant Breach</div>
          <div className={`kpi-value text-xl ${data.covenant_breaches.length > 0 ? "text-[#D0021B]" : ""}`}>
            {data.covenant_breaches.length}
          </div>
        </div>
        <div className="kpi-card" data-testid="kpi-collateral-shortfall">
          <div className="kpi-label">Collateral Shortfall</div>
          <div className={`kpi-value text-xl ${data.collateral_shortfall.length > 0 ? "text-[#D0021B]" : ""}`}>
            {data.collateral_shortfall.length}
          </div>
        </div>
      </div>

      {/* Tables row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="border border-border rounded-md bg-card overflow-hidden" data-testid="top-borrowers-card">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider">Top 10 Borrower</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Berdasarkan eksposur outstanding</p>
            </div>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Borrower</th>
                <th className="text-right">Eksposur</th>
              </tr>
            </thead>
            <tbody>
              {data.top_borrowers.map((b, i) => (
                <tr key={b.borrower_id} className="cursor-pointer" onClick={() => navigate(`/borrowers/${b.borrower_id}`)}>
                  <td>
                    <div className="font-medium">{b.name}</div>
                    <div className="text-xs text-muted-foreground mono">{b.borrower_id}</div>
                  </td>
                  <td className="text-right-num">{formatIDR(b.exposure, { compact: true })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border border-border rounded-md bg-card overflow-hidden" data-testid="top-groups-card">
          <div className="px-4 py-3 border-b border-border">
            <h3 className="text-sm font-bold uppercase tracking-wider">Top Group Obligor</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Berdasarkan eksposur konsolidasi</p>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Group</th>
                <th className="text-right">Eksposur</th>
              </tr>
            </thead>
            <tbody>
              {data.top_groups.map((g) => (
                <tr key={g.group_id} className="cursor-pointer" onClick={() => navigate(`/groups/${g.group_id}`)}>
                  <td>
                    <div className="font-medium">{g.name}</div>
                    <div className="text-xs text-muted-foreground mono">{g.group_id}</div>
                  </td>
                  <td className="text-right-num">{formatIDR(g.exposure, { compact: true })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* High utilization */}
      <div className="border border-border rounded-md bg-card overflow-hidden mb-6" data-testid="high-util-card">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider">Fasilitas Utilisasi Tinggi (≥90%)</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Perlu monitor ketat</p>
          </div>
          <SeverityBadge severity="warning" />
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Facility ID</th>
              <th>Borrower</th>
              <th>Tipe</th>
              <th className="text-right">Approved</th>
              <th className="text-right">Utilized</th>
              <th className="text-right">Util %</th>
              <th>Jatuh Tempo</th>
            </tr>
          </thead>
          <tbody>
            {data.high_utilization_facilities.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-muted-foreground py-6">Tidak ada fasilitas dengan utilisasi tinggi</td></tr>
            ) : (
              data.high_utilization_facilities.map((f) => (
                <tr key={f.facility_id} className="cursor-pointer" onClick={() => navigate(`/facilities/${f.facility_id}`)}>
                  <td className="mono">{f.facility_id}</td>
                  <td>{f.borrower_name}</td>
                  <td>{f.facility_type}</td>
                  <td className="text-right-num">{formatIDR(f.approved_limit, { compact: true })}</td>
                  <td className="text-right-num">{formatIDR(f.utilized_limit, { compact: true })}</td>
                  <td className={`text-right-num font-bold ${f.utilization_ratio > 1 ? "text-[#D0021B]" : "text-[#F5A623]"}`}>
                    {formatPercent(f.utilization_ratio)}
                  </td>
                  <td>{formatDate(f.maturity_date)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Recent alerts */}
      <div className="border border-border rounded-md bg-card overflow-hidden" data-testid="recent-alerts-card">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="text-sm font-bold uppercase tracking-wider">Recent Alerts</h3>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Tipe</th>
              <th>Severity</th>
              <th>Entity</th>
              <th>Pesan</th>
              <th>Tanggal</th>
            </tr>
          </thead>
          <tbody>
            {data.recent_alerts.slice(0, 8).map((a) => (
              <tr key={a.alert_id}>
                <td className="mono text-xs">{a.alert_type}</td>
                <td><SeverityBadge severity={a.severity} /></td>
                <td className="text-xs">{a.related_entity_type} <span className="mono text-muted-foreground">{a.related_entity_id}</span></td>
                <td className="text-xs">{a.message}</td>
                <td className="mono text-xs">{formatDate(a.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
